import React, { useState, useMemo, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { userService } from '../../../services/supabaseService';
import { data } from 'autoprefixer';

// Fonction helper pour obtenir le département selon le rôle
const getRoleDepartment = (role) => {
  const departments = {
    'tenant_admin': 'Administration',
    'manager': 'Management',
    'inspector': 'Field Operations',
    'client': 'Client',
    'viewer': 'Viewer'
  };
  return departments[role] || 'N/A';
};

// Fonction helper pour obtenir les permissions selon le rôle
const getRolePermissions = (role) => {
  const permissions = {
    'tenant_admin': ['system_admin', 'manage_users', 'manage_clients', 'view_analytics'],
    'manager': ['manage_users', 'view_analytics', 'manage_clients', 'generate_reports'],
    'inspector': ['inspect_equipment', 'generate_reports', 'mobile_access'],
    'client': ['client_portal', 'view_reports'],
    'viewer': ['view_reports']
  };
  return permissions[role] || [];
};

const UserGrid = ({
  selectedTenant,
  selectedUser,
  onUserSelect,
  selectedItems = [],
  onSelectionChange,
  searchTerm,
  onSearchChange,
  handleCreateUser,
  currentUserRole,
  currentUserId
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [filterConfig, setFilterConfig] = useState({
    role: 'all',
    status: 'all',
    mfa: 'all',
    lastLogin: 'all'
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageSizeOptions] = useState([5, 10, 25, 50, 100]);

  // Récupérer les utilisateurs depuis Supabase
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        let usersData, error, user_profile;
        console.log('Current User Role:', currentUserRole);
        if (currentUserRole === 'admin') {
           ({ data: usersData, error } = await userService.getAllUsers());
        }
        else {
            const profileResult = await userService.getUserProfile(currentUserId);
            user_profile = profileResult.data;
            ({ data: usersData, error } = await userService.getAllUsers());
            console.log('Fetched Users for Tenant Admin:', usersData);
        }
        if (error) {
          throw error;
        }

        // Transformer les données selon la structure Supabase
        const transformedUsers = usersData.map(user => ({
          id: user.id,
          name: user.full_name || 'N/A',
          email: user.email,
          phone: user.phone,
          avatar: user.avatar_url || "/assets/images/no_image.png",
          avatarAlt: `Photo de ${user.full_name}`,
          role: user.role || 'User',
          tenant: user.tenant_id || 'N/A',
          department: getRoleDepartment(user.role),
          status: user.is_active ? 'active' : 'inactive',
          lastLogin: user.last_login_at ? new Date(user.last_login_at) : new Date(user.created_at),
          mfaEnabled: false, // À implémenter si nécessaire
          equipmentAssigned: 0, // À calculer depuis une autre table si nécessaire
          inspectionsCompleted: 0, // À calculer depuis une autre table si nécessaire
          joinDate: new Date(user.created_at),
          permissions: getRolePermissions(user.role),
          // Garder les données originales
          originalData: user
        }));
        
        setUsers(transformedUsers);
        // Reset à la première page quand les données changent
        setCurrentPage(1);
      } catch (err) {
        console.error('Erreur lors du chargement des utilisateurs:', err);
        setError('Erreur lors du chargement des utilisateurs');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [currentUserRole, currentUserId]); // Dépendances mises à jour

  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Filter by tenant
    if (selectedTenant) {
      filtered = filtered?.filter((user) => user?.tenant === selectedTenant?.name);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered?.filter((user) =>
        user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        user?.role?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    // Apply filters
    if (filterConfig?.role !== 'all') {
      filtered = filtered?.filter((user) => user?.role === filterConfig?.role);
    }
    if (filterConfig?.status !== 'all') {
      filtered = filtered?.filter((user) => user?.status === filterConfig?.status);
    }
    if (filterConfig?.mfa !== 'all') {
      const mfaFilter = filterConfig?.mfa === 'enabled';
      filtered = filtered?.filter((user) => user?.mfaEnabled === mfaFilter);
    }

    return filtered;
  }, [users, selectedTenant, searchTerm, filterConfig]);

  const sortedUsers = useMemo(() => {
    const sorted = [...filteredUsers]?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredUsers, sortConfig]);

  // Calculer les données paginées
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedUsers.slice(startIndex, endIndex);
  }, [sortedUsers, currentPage, itemsPerPage]);

  // Calculer le nombre total de pages
  const totalPages = useMemo(() => {
    return Math.ceil(sortedUsers.length / itemsPerPage);
  }, [sortedUsers.length, itemsPerPage]);

  // Gérer le changement de page
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Optionnel : faire défiler vers le haut de la table
    const tableContainer = document.querySelector('.table-container');
    if (tableContainer) {
      tableContainer.scrollTop = 0;
    }
  };

  // Gérer le changement du nombre d'éléments par page
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset à la première page
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Reset à la première page quand on trie
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // Sélectionner tous les utilisateurs de la page courante
      onSelectionChange(paginatedUsers);
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectUser = (user, checked) => {
    if (checked) {
      onSelectionChange([...selectedItems, user]);
    } else {
      onSelectionChange(selectedItems?.filter((item) => item?.id !== user?.id));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-success/10 text-success border-success/20',
      inactive: 'bg-muted text-muted-foreground border-border',
      suspended: 'bg-error/10 text-error border-error/20'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${styles?.[status]}`}>
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const formatLastLogin = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const allSelected = paginatedUsers?.length > 0 && 
    paginatedUsers.every(user => selectedItems.some(item => item?.id === user?.id));
  const someSelected = paginatedUsers?.length > 0 && 
    paginatedUsers.some(user => selectedItems.some(item => item?.id === user?.id)) && 
    !allSelected;

  // Afficher un état de chargement
  if (loading) {
    return (
      <div className="h-full bg-card flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="animate-spin text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Chargement des utilisateurs...</p>
        </div>
      </div>
    );
  }

  // Afficher une erreur
  if (error) {
    return (
      <div className="h-full bg-card flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-error mb-4" />
          <p className="text-error mb-2">{error}</p>
          <Button 
            variant="outline" 
            onClick={() => window.location.reload()}
          >
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-card flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">User Management</h3>
            <p className="text-sm text-muted-foreground">
              {sortedUsers?.length} Utilisateurs {selectedTenant ? `in ${selectedTenant?.name}` : 'total'}
              {sortedUsers?.length > 0 && ` • Page ${currentPage} sur ${totalPages}`}
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button variant="default" iconName="UserPlus" iconPosition="left" iconSize={16} onClick={handleCreateUser}>
              Nouveau utilisateur
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Rechercher des utilisateurs par nom, e-mail ou rôle..."
              value={searchTerm}
              onChange={(e) => {
                onSearchChange(e?.target?.value);
                setCurrentPage(1); // Reset à la première page quand on recherche
              }}
              className="w-full"
            />
          </div>
          
          <select
            value={filterConfig?.status}
            onChange={(e) => {
              setFilterConfig((prev) => ({ ...prev, status: e?.target?.value }));
              setCurrentPage(1); // Reset à la première page quand on filtre
            }}
            className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
            <option value="suspended">Suspendu</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto table-container">
        <table className="w-full">
          <thead className="bg-muted/50 sticky top-0">
            <tr>
              <th className="w-12 p-3 text-left">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="p-3 text-left">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('name')}
                  className="h-auto p-0 font-medium text-foreground hover:text-primary"
                >
                  utilisateur
                  <Icon
                    name={sortConfig?.key === 'name' && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'}
                    size={14}
                    className="ml-1"
                  />
                </Button>
              </th>
              <th className="p-3 text-left">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('role')}
                  className="h-auto p-0 font-medium text-foreground hover:text-primary"
                >
                  Role & Département
                  <Icon
                    name={sortConfig?.key === 'role' && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'}
                    size={14}
                    className="ml-1"
                  />
                </Button>
              </th>
              <th className="p-3 text-left">Statut</th>
              <th className="p-3 text-left">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('lastLogin')}
                  className="h-auto p-0 font-medium text-foreground hover:text-primary"
                >
                  Dernière connexion
                  <Icon
                    name={sortConfig?.key === 'lastLogin' && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'}
                    size={14}
                    className="ml-1"
                  />
                </Button>
              </th>
              <th className="w-12 p-3"></th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers?.map((user) => {
              const isSelected = selectedItems?.some((item) => item?.id === user?.id);
              const isCurrentUser = selectedUser?.id === user?.id;

              return (
                <tr
                  key={user?.id}
                  className={`border-b border-border hover:bg-muted/50 cursor-pointer transition-micro ${
                    isCurrentUser ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => onUserSelect(user)}
                >
                  <td className="p-3">
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => {
                        e?.stopPropagation();
                        handleSelectUser(user, e?.target?.checked);
                      }}
                    />
                  </td>
                  <td className="p-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex-shrink-0">
                        <img
                          src={user?.avatar}
                          alt={user?.avatarAlt}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '/assets/images/no_image.png';
                          }}
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">{user?.role}</p>
                      <p className="text-xs text-muted-foreground">{user?.department}</p>
                    </div>
                  </td>
                  <td className="p-3">
                    {getStatusBadge(user?.status)}
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-muted-foreground">
                      {formatLastLogin(user?.lastLogin)}
                    </span>
                  </td>
                  <td className="p-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e?.stopPropagation();
                        // Handle menu action
                      }}
                    >
                      <Icon name="MoreVertical" size={16} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {paginatedUsers?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <Icon name="Users" size={48} className="text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No users found</h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              {searchTerm ? 'Try adjusting your search terms or filters.' : 'Get started by adding your first user.'}
            </p>
          </div>
        )}
      </div>

      {/* Pagination Footer */}
      {sortedUsers.length > 0 && (
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">Lignes par page:</span>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="px-2 py-1 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {pageSizeOptions.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <span className="text-sm text-muted-foreground">
                {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, sortedUsers.length)} sur {sortedUsers.length}
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                iconName="ChevronsLeft"
                iconSize={16}
              >
                Première
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                iconName="ChevronLeft"
                iconSize={16}
              >
                Précédent
              </Button>
              
              {/* Pages numbers */}
              <div className="flex items-center space-x-1">
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  // Afficher seulement quelques pages autour de la page courante
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={currentPage === pageNumber ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                        className="min-w-[32px]"
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 3 ||
                    pageNumber === currentPage + 3
                  ) {
                    return <span key={pageNumber} className="px-2">...</span>;
                  }
                  return null;
                })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                iconName="ChevronRight"
                iconPosition="right"
                iconSize={16}
              >
                Suivant
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                iconName="ChevronsRight"
                iconPosition="right"
                iconSize={16}
              >
                Dernière
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Selection Footer */}
      {selectedItems?.length > 0 && (
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">
              {selectedItems?.length} user{selectedItems?.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" iconName="Mail" iconPosition="left" iconSize={14}>
                Envoyer un e-mail
              </Button>
              <Button variant="outline" size="sm" iconName="UserCog" iconPosition="left" iconSize={14}>
                Édition en lot
              </Button>
              <Button variant="outline" size="sm" iconName="Download" iconPosition="left" iconSize={14}>
                Exporter
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserGrid;